import { MonitorSmartphone, Moon, Sun } from 'lucide-react'
import { cn } from '@/lib/utils'
import { THEME_OPTIONS, useTheme, type ThemePref } from '@/lib/theme'

/** 三态图标。用 lucide 现成图标，不新增图标资源。 */
const ICONS: Record<ThemePref, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: MonitorSmartphone,
}

/**
 * 主题三态切换（浅色 / 深色 / 跟随系统）。
 *
 * 紧凑分段控件：三个图标按钮拼一个 radiogroup，只用令牌 + cn，不新增 UI 原语。
 * 「跟随系统」的 title 里带上当前实际解析结果，避免用户看不出系统现在是哪种。
 *
 * ⚠️ 本文件与另一端 frontend 的同名文件刻意保持相同（两端设计系统同源），改动请同步。
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { pref, resolved, setTheme } = useTheme()

  return (
    <div
      role="radiogroup"
      aria-label="主题"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-md bg-[var(--color-muted)] p-0.5',
        className,
      )}
    >
      {THEME_OPTIONS.map(({ value, label }) => {
        const Icon = ICONS[value]
        const active = pref === value
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            title={
              value === 'system'
                ? `${label}（当前：${resolved === 'dark' ? '深色' : '浅色'}）`
                : label
            }
            onClick={() => setTheme(value)}
            className={cn(
              'grid size-6 place-items-center rounded-[5px] transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
              active
                ? 'bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm'
                : 'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]',
            )}
          >
            <Icon className="size-3.5" />
            <span className="sr-only">{label}</span>
          </button>
        )
      })}
    </div>
  )
}
